import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../../api/ad.api';
import { StrategyDailyMetric } from '../../../types/Ad.api.type';
import { Card } from '../../ui/card';
import { KpiChart } from './charts/KpiChart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const POSITION_LABELS: Record<string, string> = {
  MAIN_FEED: '메인 피드',
  MAIN_FEED_TOP: '메인 피드 상단',
  POPULAR_FEED: '인기설문 피드',
  POPULAR_FEED_TOP: '인기설문 피드 상단',
  MY_FEED: '내설문 피드',
  MY_FEED_TOP: '내설문 피드 상단',
  DETAIL_COMMENT: '상세 댓글 위',
  ATTENDANCE: '출석 달력 하단',
  BENEFIT: '혜택 페이지',
  MAIN_FEED_IMAGE: '메인 피드 이미지',
  OTHER: '기타 (미분리)',
};

const POSITION_COLORS = [
  '#3b82f6',
  '#f97316',
  '#10b981',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f59e0b',
  '#6366f1',
  '#06b6d4',
  '#78716c',
];

const FIELD_LABELS: Record<string, string> = {
  revenue: '수익 (원)',
  impressions: '노출',
  ecpm: 'eCPM (원)',
  ctr: '시청률 (%)',
};

const getDefaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
};

export const TossAnalysisTab = () => {
  const defaultRange = getDefaultRange();
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);

  const { data: metricsData, isLoading } = useQuery({
    queryKey: ['strategyMetrics', from, to],
    queryFn: () => adApi.getStrategyMetrics(from, to).then((res) => res.data),
    meta: { hideGlobalLoader: true },
  });

  const { data: deploys } = useQuery({
    queryKey: ['strategyDeploys'],
    queryFn: () => adApi.getDeploys().then((res) => res.data),
    meta: { hideGlobalLoader: true },
  });

  const metrics = useMemo(() => metricsData?.data ?? [], [metricsData]);

  // 위치별 데이터가 있는지 확인
  const hasPositionData = useMemo(
    () => metrics.some((m) => m.bannerPositions && m.bannerPositions.length > 0),
    [metrics],
  );

  const hasImagePositionData = useMemo(
    () => metrics.some((m) => m.imagePositions && m.imagePositions.length > 0),
    [metrics],
  );

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-6">
      {/* 기간 선택 */}
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="h-8 px-2 text-xs border rounded bg-white"
        />
        <span className="text-gray-400">~</span>
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="h-8 px-2 text-xs border rounded bg-white"
        />
      </div>

      {/* 타입별 비교 차트 (리워드 vs 배너 vs 이미지배너) */}
      {metrics.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <KpiChart
            title="노출 추이"
            data={metrics}
            dataKey="tossRewardImpressions"
            dataKey2="tossBannerImpressions"
            dataKey3="tossImageImpressions"
            dataKey4="tossInterstitialImpressions"
            color="#3b82f6"
            color2="#f97316"
            color3="#10b981"
            color4="#f43f5e"
            label1="리워드"
            label2="배너"
            label3="이미지배너"
            label4="전면광고"
            deploys={deploys}
          />
          <KpiChart
            title="시청률 추이 (%)"
            data={metrics}
            dataKey="tossRewardCtr"
            dataKey2="tossBannerCtr"
            dataKey3="tossImageCtr"
            dataKey4="tossInterstitialCtr"
            color="#3b82f6"
            color2="#f97316"
            color3="#10b981"
            color4="#f43f5e"
            label1="리워드"
            label2="배너"
            label3="이미지배너"
            label4="전면광고"
            deploys={deploys}
          />
          <KpiChart
            title="eCPM 추이 (원)"
            data={metrics}
            dataKey="tossRewardEcpm"
            dataKey2="tossBannerEcpm"
            dataKey3="tossImageEcpm"
            dataKey4="tossInterstitialEcpm"
            color="#3b82f6"
            color2="#f97316"
            color3="#10b981"
            color4="#f43f5e"
            label1="리워드"
            label2="배너"
            label3="이미지배너"
            label4="전면광고"
            deploys={deploys}
          />
          <KpiChart
            title="수익 추이 (원)"
            data={metrics}
            dataKey="tossRewardRevenue"
            dataKey2="tossBannerRevenue"
            dataKey3="tossImageRevenue"
            dataKey4="tossInterstitialRevenue"
            color="#3b82f6"
            color2="#f97316"
            color3="#10b981"
            color4="#f43f5e"
            label1="리워드"
            label2="배너"
            label3="이미지배너"
            label4="전면광고"
            deploys={deploys}
          />

          {/* 리텐션 차트 */}
          <RetentionChart data={metrics} />

          {/* 유저 차트 */}
          <UserChart data={metrics} />
        </div>
      )}

      {/* 위치별 배너 상세 차트 + 테이블 */}
      {hasPositionData && (
        <>
          <h4 className="text-sm font-bold text-gray-900 mt-6">배너 위치별 상세</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BannerByPositionChart data={metrics} field="revenue" source="banner" />
            <BannerByPositionChart data={metrics} field="impressions" source="banner" />
            <BannerByPositionChart data={metrics} field="ecpm" source="banner" />
            <BannerByPositionChart data={metrics} field="ctr" source="banner" />
          </div>
          <PositionTable data={metrics} source="banner" />
        </>
      )}

      {/* 위치별 이미지배너 상세 차트 + 테이블 */}
      {hasImagePositionData && (
        <>
          <h4 className="text-sm font-bold text-emerald-700 mt-6">이미지배너 위치별 상세</h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BannerByPositionChart data={metrics} field="revenue" source="image" />
            <BannerByPositionChart data={metrics} field="impressions" source="image" />
            <BannerByPositionChart data={metrics} field="ecpm" source="image" />
            <BannerByPositionChart data={metrics} field="ctr" source="image" />
          </div>
          <PositionTable data={metrics} source="image" />
        </>
      )}
    </div>
  );
};

// ==================== 위치별 배너 차트 ====================

const BannerByPositionChart = ({
  data,
  field,
  source = 'banner',
}: {
  data: StrategyDailyMetric[];
  field: 'revenue' | 'impressions' | 'ecpm' | 'ctr';
  source?: 'banner' | 'image';
}) => {
  const pick = (m: StrategyDailyMetric) => (source === 'image' ? m.imagePositions : m.bannerPositions);

  const allPositions = useMemo(() => {
    const set = new Set<string>();
    data.forEach((d) => pick(d)?.forEach((bp) => set.add(bp.position)));
    return Array.from(set);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, source]);

  const chartData = useMemo(
    () =>
      data
        .filter((d) => {
          const pos = pick(d);
          return pos && pos.length > 0;
        })
        .map((d) => {
          const row: Record<string, unknown> = { date: d.date.slice(5) };
          pick(d)?.forEach((bp) => {
            row[bp.position] = bp[field];
          });
          return row;
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, field, source],
  );

  if (chartData.length === 0) return null;

  return (
    <Card>
      <div className="p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">위치별 {FIELD_LABELS[field] ?? field}</h4>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            {allPositions.map((pos, i) => (
              <Line
                key={pos}
                type="monotone"
                dataKey={pos}
                stroke={POSITION_COLORS[i % POSITION_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls
                name={POSITION_LABELS[pos] ?? pos}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// ==================== 위치별 테이블 (배너/이미지 겸용) ====================

const PositionTable = ({ data, source = 'banner' }: { data: StrategyDailyMetric[]; source?: 'banner' | 'image' }) => {
  const pick = (m: StrategyDailyMetric) => (source === 'image' ? m.imagePositions : m.bannerPositions);

  const getTotal = (m: StrategyDailyMetric) =>
    source === 'image'
      ? { imp: m.tossImageImpressions, ctr: m.tossImageCtr, ecpm: m.tossImageEcpm, rev: m.tossImageRevenue }
      : { imp: m.tossBannerImpressions, ctr: m.tossBannerCtr, ecpm: m.tossBannerEcpm, rev: m.tossBannerRevenue };

  const rows = useMemo(
    () =>
      data.filter((d) => {
        const pos = pick(d);
        return pos && pos.length > 0;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, source],
  );

  const allPositions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((d) => pick(d)?.forEach((bp) => set.add(bp.position)));
    return Array.from(set);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, source]);

  if (rows.length === 0) return null;

  const title = source === 'image' ? '위치별 이미지배너 데이터' : '위치별 배너 데이터';

  return (
    <Card>
      <div className="p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 pr-2 whitespace-nowrap">날짜</th>
                {allPositions.map((pos) => (
                  <th key={pos} colSpan={4} className="pb-1 text-center whitespace-nowrap border-l border-gray-200">
                    <span className="text-[10px] font-semibold">{POSITION_LABELS[pos] ?? pos}</span>
                  </th>
                ))}
                <th colSpan={4} className="pb-1 text-center whitespace-nowrap border-l border-gray-300 bg-gray-50">
                  <span className="text-[10px] font-semibold">합산</span>
                </th>
              </tr>
              <tr className="border-b text-gray-400">
                <th className="pb-2 pr-2" />
                {allPositions.map((pos) => (
                  <React.Fragment key={pos}>
                    <th className="pb-2 px-1 text-right text-[10px] border-l border-gray-200">노출</th>
                    <th className="pb-2 px-1 text-right text-[10px]">CTR</th>
                    <th className="pb-2 px-1 text-right text-[10px]">eCPM</th>
                    <th className="pb-2 px-1 text-right text-[10px]">수익</th>
                  </React.Fragment>
                ))}
                <th className="pb-2 px-1 text-right text-[10px] border-l border-gray-300">노출</th>
                <th className="pb-2 px-1 text-right text-[10px]">CTR</th>
                <th className="pb-2 px-1 text-right text-[10px]">eCPM</th>
                <th className="pb-2 px-1 text-right text-[10px]">수익</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const total = getTotal(d);
                return (
                  <tr key={d.date} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{d.date.slice(5)}</td>
                    {allPositions.map((pos) => {
                      const bp = pick(d)?.find((b) => b.position === pos);
                      return (
                        <React.Fragment key={pos}>
                          <td className="py-1.5 px-1 text-right font-mono border-l border-gray-100">
                            {bp?.impressions ?? '-'}
                          </td>
                          <td className="py-1.5 px-1 text-right font-mono">{bp?.ctr != null ? `${bp.ctr}%` : '-'}</td>
                          <td className="py-1.5 px-1 text-right font-mono">{bp?.ecpm ?? '-'}</td>
                          <td className="py-1.5 px-1 text-right font-mono">{bp?.revenue ?? '-'}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className="py-1.5 px-1 text-right font-mono font-semibold border-l border-gray-200 bg-gray-50">
                      {total.imp ?? '-'}
                    </td>
                    <td className="py-1.5 px-1 text-right font-mono font-semibold bg-gray-50">
                      {total.ctr != null ? `${total.ctr}%` : '-'}
                    </td>
                    <td className="py-1.5 px-1 text-right font-mono font-semibold bg-gray-50">{total.ecpm ?? '-'}</td>
                    <td className="py-1.5 px-1 text-right font-mono font-semibold bg-gray-50">{total.rev ?? '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

// ==================== 리텐션 차트 ====================

const RetentionChart = ({ data }: { data: StrategyDailyMetric[] }) => {
  const chartData = data.map((d) => ({
    date: d.date.slice(5),
    retentionD1New: d.retentionD1New,
    retentionD1Total: d.retentionD1Total,
  }));

  return (
    <Card>
      <div className="p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">D1 리텐션 추이 (%)</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} unit="%" />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="retentionD1New"
              stroke="#ec4899"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              name="신규 D1"
            />
            <Line
              type="monotone"
              dataKey="retentionD1Total"
              stroke="#a855f7"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              name="기존 D1"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// ==================== 유저 차트 ====================

const UserChart = ({ data }: { data: StrategyDailyMetric[] }) => {
  const chartData = data.map((d) => ({
    date: d.date.slice(5),
    newUsers: d.newUsers,
    totalUsers: d.totalUsers,
  }));

  return (
    <Card>
      <div className="p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">유저 추이</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="newUsers"
              stroke="#f97316"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              name="신규"
            />
            <Line
              type="monotone"
              dataKey="totalUsers"
              stroke="#6366f1"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              name="전체"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
